import { useEffect, useLayoutEffect } from 'react';

type PageMetaProps = {
  title?: string;
  desktopTabIcon?: string | null;
};

/**
 * Sets page metadata including document title and desktop app tab icon.
 *
 * Implemented as a component rather than a hook to allow declarative placement
 * in the component tree for predictable execution order. For example, in the code
 * below, the title set in `PageRoot` will be used as a fallback. If `PageMeta` were
 * a hook, the hook in `PageRoot` would run after the one in `PageContent`, so it would
 * override the title set in `PageContent`.
 *
 * ```jsx
 * const PageRoot = ({ routeName }) => {
 *   return (
 *     <>
 *       <PageMeta title={routeName} />
 *       <PageContent />
 *     </>
 *   );
 * };
 *
 * const PageContent = () => {
 *   return (
 *     <div>
 *       <PageMeta title="My Page" />
 *     </div>
 *   );
 * };
 * ```
 */
const PageMeta = ({ title, desktopTabIcon }: PageMetaProps) => {
  useLayoutEffect(() => {
    if (title) {
      document.title = title;
    }
  }, [title]);

  useEffect(() => {
    window.desktop?.setDesktopTabTitle?.(title ?? '');
  }, [title]);

  useEffect(() => {
    if (desktopTabIcon) {
      window.desktop?.setDesktopTabIcon?.(desktopTabIcon);
    }
    return () => {
      window.desktop?.setDesktopTabIcon?.('');
    };
  }, [desktopTabIcon]);

  return null;
};

export default PageMeta;
